import React, {
    useState,
    useEffect,
    ReactNode
} from 'react';

import { v4 as uuid } from 'uuid';

// Material ui
import {
    Grid,
    Table as MaterialTable,
    TableBody,
    TableRow
} from '@material-ui/core';

// Types
import { Action } from '@components/UI-KIT/ContextMenu';

// Components
import Pagination from '@components/UI-KIT/Table/Pagination';
import GenericTableHead from '@components/UI-KIT/Table/GenericTableHead';
import GenericTableRow from '@components/UI-KIT/Table/GenericTableRow';
import TableFilters from '@components/UI-KIT/Table/TableFilters';
import Typography, { TypographyColor } from '@components/UI-KIT/Typography';
import SearchInput from '@components/UI-KIT/Inputs/SearchInput';
import Spinner from '@components/UI-KIT/Spinner';

// Styles
import './Table.scss';

export const ModalContext = React.createContext({});

let timeout: NodeJS.Timeout; // Used for search terms

export type Item = any;

export type FetchStatus = 'pending' | 'ready' | 'error';

export interface GenericItem {
    id: string | number;
}

export interface TableHead {
    label: string | JSX.Element;
    field?: string;
    link?: {
        href: string;
        as: string;
        field: string;
    };
    type?: 'custom' | 'nested' | 'composed' | 'quantity';
    handleValue?: (_item: unknown) => string | number | JSX.Element;
    fields?: string[];
    valuesSeparator?: string;
    format?: 'date' | 'amount' | 'dni';
    sortable?: boolean;
}

interface CheckColumn {
    label: string | JSX.Element;
    setValue: (_value: unknown) => void;
}

export interface SortHandler {
    field?: string; // Field to give to the backend
    columnIndex: number;
    direction: 'ASC' | 'DESC';
}

export interface CheckColumnWithoutId extends CheckColumn {
    value: Item;
}

export interface CheckColumnWithId extends CheckColumn {
    values: Item[];
}

export interface TableData {
    items: Item[];
    totalItems: number;
}

interface Props<T extends GenericItem> {
    pagination?: boolean;
    searchbar?: {
        action: (_value: string) => void | undefined;
        placeholder?: string;
        term: string;
    };
    filters?: ReactNode;
    columns?: ReactNode;
    download?: string;
    downloadExcel?: () => void;
    disableDownloadExcel?: boolean;
    page: number;
    size: number;
    handleSizeChange: (_value: number) => void;
    handlePageChange: (_value: number) => void;
    rowsPerPage?: number[];
    actions?: Action<T>[];
    childrenAction?: (_item: T) => ReactNode;
    tableData: TableData;
    tableHead: TableHead[];
    handleTableHeadSortClick?: (_columnIndex: number) => void;
    checkFromTheRight?: CheckColumnWithoutId;
    checkFromTheLeft?: CheckColumnWithoutId;
    checkWithId?: CheckColumnWithId;
    title?: string | ReactNode;
    titleColor?: TypographyColor;
    fetchStatus: string;
    customRow?: ReactNode;
    hideActions?: (_item: Item) => boolean;
    filtersApplied?: boolean;
    sortHandler?: SortHandler;
}

function Table<T extends GenericItem>({
    tableData,
    tableHead,
    actions,
    childrenAction,
    size,
    page,
    pagination = true,
    filters,
    columns,
    download,
    handleSizeChange,
    handlePageChange,
    checkFromTheRight = undefined,
    checkFromTheLeft = undefined,
    checkWithId,
    title,
    searchbar,
    fetchStatus,
    filtersApplied,
    rowsPerPage = [
        20,
        50,
        100
    ],
    customRow,
    hideActions,
    downloadExcel,
    disableDownloadExcel,
    titleColor,
    sortHandler,
    handleTableHeadSortClick
}: Props<T>) {
    const [ searchTerm, setSearchTerm ] = useState('');

    useEffect(() => () => {
        if (!searchbar) return;
        setSearchTerm(searchbar.term);
    }, [ ]);

    useEffect(() => {
        if (!searchbar) return;
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => searchbar.action(searchTerm), 300);
    }, [ searchTerm ]);

    if (![
        'pending',
        'error',
        'ready'
    ].includes(fetchStatus)) throw new Error('FetchStatus must be one of "pending", "error" or "ready"');

    return (
        <>
            {(title || filters || columns || download || downloadExcel) && (
                <Grid
                    container
                    justify="space-between"
                    alignItems="center"
                    className="ui-Table-head"
                >
                    <Grid
                        item
                        className="ui-Table-title"
                        xs={6}
                    >
                        { title && (
                            <Typography
                                variant="h3"
                                color={titleColor ? titleColor : 'secondary'}
                            >
                                {title}
                            </Typography>
                        )}
                    </Grid>
                    <Grid
                        item
                        container
                        alignItems="center"
                        xs={6}
                    >
                        { (filters || columns || download || downloadExcel) && (
                            <TableFilters
                                filtersApplied={filtersApplied}
                                filters={filters}
                                columns={columns}
                                download={download}
                                downloadExcel={downloadExcel}
                                disableDownloadExcel={disableDownloadExcel}
                            />
                        )}
                    </Grid>
                </Grid>
            )}
            { searchbar && (
                <SearchInput
                    xs={12}
                    placeholder={searchbar.placeholder || 'Buscar...'}
                    onChange={(value: string) => {
                        setSearchTerm(value);
                        handlePageChange(1);
                    }}
                    value={searchTerm || ''}
                />
            )}
            <Grid
                className="ui-Table-container"
            >
                <>
                    { (fetchStatus === 'ready' && (tableData.items.length > 0 || customRow)) && (
                        <MaterialTable
                            className="ui-Table-element"
                        >
                            { tableData.items.length > 0 && (
                                <GenericTableHead
                                    tableHead={tableHead}
                                    checkFromTheRight={checkFromTheRight}
                                    checkFromTheLeft={checkFromTheLeft}
                                    checkWithId={checkWithId}
                                    actions={actions}
                                    sortHandler={sortHandler}
                                    handleTableHeadSortClick={handleTableHeadSortClick}
                                />
                            )}
                            <TableBody
                                className="ui-Table-body"
                            >
                                { tableData.items.map((item, index) => (
                                    <GenericTableRow
                                        key={item.id || uuid()}
                                        item={item}
                                        index={index}
                                        checkFromTheRight={checkFromTheRight}
                                        checkFromTheLeft={checkFromTheLeft}
                                        checkWithId={checkWithId}
                                        tableHead={tableHead}
                                        actions={actions}
                                        childrenAction={childrenAction}
                                        hideActions={hideActions}
                                    />
                                ))}
                                { customRow && (
                                    <TableRow
                                        className="ui-Table-custom-row-component"
                                    >
                                        { customRow }
                                    </TableRow>
                                )}
                            </TableBody>
                        </MaterialTable>
                    )}
                    { fetchStatus === 'pending' && (
                        <Spinner />
                    )}
                    {(
                        pagination
                            && fetchStatus === 'ready'
                            && tableData.totalItems > 0
                    ) && (
                        <Pagination
                            page={page}
                            size={size}
                            rowsPerPage={rowsPerPage}
                            handlePageChange={handlePageChange}
                            handleSizeChange={handleSizeChange}
                            totalCount={tableData.totalItems}
                        />
                    )}
                </>
                {(
                    fetchStatus === 'ready'
                        && (searchbar?.term || filtersApplied)
                        && tableData.totalItems <= 0
                ) && (
                    <Grid
                        container
                        justify="flex-start"
                        alignItems="center"
                        style={{ marginTop: '.5rem' }}
                    >
                        <img
                            alt="Lupa"
                            src="/images/magnifying-glass.svg"
                            style={{ marginRight: '1.5rem' }}
                        />
                        <Typography>
                            No se encontraron elementos en tu búsqueda
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export default Table;
